// IndexedDB offline storage service for React 19 with TypeScript

// Type definitions
interface VideoRequestData {
  userEmail: string;
  data: any;
  timestamp: number;
}

interface PhoneRequestData {
  userEmail: string;
  data: any;
  timestamp: number;
}

interface CountryData {
  language: string;
  data: any;
  timestamp: number;
}

interface AmosTokenRecord {
  userEmail: string;
  amosToken: string;
  amosRefreshToken: string;
  amosClient: string;
  timestamp: number;
}

interface ClientDataRecord {
  userEmail: string;
  client: any;
  dependents: any[];
  adults: any[];
  timestamp: number;
}

interface UserAuthRecord {
  userEmail: string;
  userData: any;
  auth0User: any;
  timestamp: number;
}

interface AmosTokenData {
  amosToken: string;
  amosRefreshToken: string;
  amosClient: string;
  timestamp?: number;
}

interface ClientDataResponse {
  client: any;
  dependents: any[];
  adults: any[];
  timestamp: number;
}

interface UserAuthResponse {
  userData: any;
  auth0User: any;
  timestamp: number;
}

class OfflineStorageService {
  private dbName: string;
  private version: number;
  private db: IDBDatabase | null;

  constructor() {
    this.dbName = 'TeleconsultationDB';
    this.version = 5; // Increment version to add userAuth store
    this.db = null;
  }

  async init(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store for video calls - delete old and recreate with new keyPath
        if (db.objectStoreNames.contains('videoRequests')) {
          db.deleteObjectStore('videoRequests');
        }
        const videoStore = db.createObjectStore('videoRequests', {
          keyPath: 'userEmail',
        });
        videoStore.createIndex('timestamp', 'timestamp', { unique: false });

        // Store for phone calls - delete old and recreate with new keyPath
        if (db.objectStoreNames.contains('phoneRequests')) {
          db.deleteObjectStore('phoneRequests');
        }
        const phoneStore = db.createObjectStore('phoneRequests', {
          keyPath: 'userEmail',
        });
        phoneStore.createIndex('timestamp', 'timestamp', { unique: false });

        // Store for countries data
        if (!db.objectStoreNames.contains('countries')) {
          const countriesStore = db.createObjectStore('countries', {
            keyPath: 'language',
          });
          countriesStore.createIndex('timestamp', 'timestamp', {
            unique: false,
          });
        }

        // Store for amosToken with user email as key
        if (!db.objectStoreNames.contains('amosTokens')) {
          const tokensStore = db.createObjectStore('amosTokens', {
            keyPath: 'userEmail',
          });
          tokensStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Store for client and dependents data
        if (!db.objectStoreNames.contains('clientData')) {
          const clientStore = db.createObjectStore('clientData', {
            keyPath: 'userEmail',
          });
          clientStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Store for user authentication data (userData + auth0User)
        if (!db.objectStoreNames.contains('userAuth')) {
          const userAuthStore = db.createObjectStore('userAuth', {
            keyPath: 'userEmail',
          });
          userAuthStore.createIndex('timestamp', 'timestamp', {
            unique: false,
          });
        }
      };
    });
  }

  async storeVideoRequests(userEmail: string, data: any): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['videoRequests'], 'readwrite');
      const store = transaction.objectStore('videoRequests');

      // Simply store the complete combined data
      // (Component already combines client + adult data before calling this)
      const record: VideoRequestData = {
        userEmail,
        data,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const request = store.put(record);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error storing video requests:', error);
    }
  }

  async getVideoRequests(userEmail: string): Promise<any | null> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['videoRequests'], 'readonly');
      const store = transaction.objectStore('videoRequests');

      return new Promise((resolve, reject) => {
        const request = store.get(userEmail);
        request.onsuccess = () => resolve(request.result?.data || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting video requests:', error);
      return null;
    }
  }

  async storePhoneRequests(userEmail: string, data: any): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['phoneRequests'], 'readwrite');
      const store = transaction.objectStore('phoneRequests');

      // Simply store the complete combined data
      // (Component already combines client + adult data before calling this)
      const record: PhoneRequestData = {
        userEmail,
        data,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const request = store.put(record);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error storing phone requests:', error);
    }
  }

  async getPhoneRequests(userEmail: string): Promise<any | null> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['phoneRequests'], 'readonly');
      const store = transaction.objectStore('phoneRequests');

      return new Promise((resolve, reject) => {
        const request = store.get(userEmail);
        request.onsuccess = () => resolve(request.result?.data || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting phone requests:', error);
      return null;
    }
  }

  async storeCountries(language: string, data: any): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['countries'], 'readwrite');
      const store = transaction.objectStore('countries');

      const record: CountryData = {
        language,
        data,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const request = store.put(record);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error storing countries:', error);
    }
  }

  async getCountries(language: string): Promise<any | null> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['countries'], 'readonly');
      const store = transaction.objectStore('countries');

      return new Promise((resolve, reject) => {
        const request = store.get(language);
        request.onsuccess = () => resolve(request.result?.data || null);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting countries:', error);
      return null;
    }
  }

  // Store amosToken with user email as key
  async storeAmosToken(
    userEmail: string,
    amosToken: string,
    amosRefreshToken: string,
    amosClient: string
  ): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['amosTokens'], 'readwrite');
      const store = transaction.objectStore('amosTokens');

      const record: AmosTokenRecord = {
        userEmail,
        amosToken,
        amosRefreshToken,
        amosClient,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const request = store.put(record);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error storing amosToken:', error);
    }
  }

  // Get amosToken by user email
  async getAmosToken(userEmail: string): Promise<AmosTokenData | null> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['amosTokens'], 'readonly');
      const store = transaction.objectStore('amosTokens');

      return new Promise((resolve, reject) => {
        const request = store.get(userEmail);
        request.onsuccess = () => {
          const result = request.result as AmosTokenRecord | undefined;
          if (result) {
            resolve({
              amosToken: result.amosToken,
              amosRefreshToken: result.amosRefreshToken,
              amosClient: result.amosClient,
              timestamp: result.timestamp,
            });
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting amosToken:', error);
      return null;
    }
  }

  // Delete amosToken for a specific user
  async deleteAmosToken(userEmail: string): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['amosTokens'], 'readwrite');
      const store = transaction.objectStore('amosTokens');

      return new Promise((resolve, reject) => {
        const request = store.delete(userEmail);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error deleting amosToken:', error);
    }
  }

  async clearOldData(maxAge: number = 7 * 24 * 60 * 60 * 1000): Promise<void> {
    // Clear data older than 7 days
    try {
      if (!this.db) await this.init();

      const cutoffTime = Date.now() - maxAge;
      const stores = [
        'videoRequests',
        'phoneRequests',
        'countries',
        'amosTokens',
        'clientData',
        'userAuth',
      ];

      for (const storeName of stores) {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const index = store.index('timestamp');

        const range = IDBKeyRange.upperBound(cutoffTime);
        const request = index.openCursor(range);

        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest<IDBCursorWithValue | null>)
            .result;
          if (cursor) {
            cursor.delete();
            cursor.continue();
          }
        };
      }
    } catch (error) {
      console.error('Error clearing old data:', error);
    }
  }

  // Store client and dependents data
  async storeClientData(
    userEmail: string,
    client: any,
    dependents: any[],
    adults: any[]
  ): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['clientData'], 'readwrite');
      const store = transaction.objectStore('clientData');

      const data: ClientDataRecord = {
        userEmail,
        client,
        dependents,
        adults,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const request = store.put(data);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error storing client data:', error);
    }
  }

  // Get client and dependents data
  async getClientData(userEmail: string): Promise<ClientDataResponse | null> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['clientData'], 'readonly');
      const store = transaction.objectStore('clientData');

      return new Promise((resolve, reject) => {
        const request = store.get(userEmail);
        request.onsuccess = () => {
          const result = request.result as ClientDataRecord | undefined;
          if (result) {
            resolve({
              client: result.client,
              dependents: result.dependents,
              adults: result.adults,
              timestamp: result.timestamp,
            });
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting client data:', error);
      return null;
    }
  }

  // Store user authentication data (userData + auth0User)
  async storeUserAuth(
    userEmail: string,
    userData: any,
    auth0User: any
  ): Promise<void> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['userAuth'], 'readwrite');
      const store = transaction.objectStore('userAuth');

      const data: UserAuthRecord = {
        userEmail,
        userData,
        auth0User,
        timestamp: Date.now(),
      };

      return new Promise((resolve, reject) => {
        const request = store.put(data);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error storing user auth data:', error);
    }
  }

  // Get user authentication data
  async getUserAuth(userEmail: string): Promise<UserAuthResponse | null> {
    try {
      if (!this.db) await this.init();

      const transaction = this.db!.transaction(['userAuth'], 'readonly');
      const store = transaction.objectStore('userAuth');

      return new Promise((resolve, reject) => {
        const request = store.get(userEmail);
        request.onsuccess = () => {
          const result = request.result as UserAuthRecord | undefined;
          if (result) {
            resolve({
              userData: result.userData,
              auth0User: result.auth0User,
              timestamp: result.timestamp,
            });
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error('Error getting user auth data:', error);
      return null;
    }
  }

  async hasOfflineData(userEmail: string): Promise<boolean> {
    try {
      const [videoData, phoneData] = await Promise.all([
        this.getVideoRequests(userEmail),
        this.getPhoneRequests(userEmail),
      ]);
      return !!(videoData || phoneData);
    } catch (error) {
      console.error('Error checking offline data:', error);
      return false;
    }
  }
}

export const offlineStorage = new OfflineStorageService();
